import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI, usersAPI } from '../../services/api';
import { 
  FiAlertTriangle, FiTrash2, FiPauseCircle, FiShield, FiBell,
  FiEye, FiEyeOff, FiLock, FiGlobe, FiMoon, FiSun, FiDownload
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './AccountSettings.css';

const AccountSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('privacy'); // privacy, notifications, security, preferences, account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const DEFAULT_PRIVACY_SETTINGS = {
    profileVisible: true,
    showEmail: false,
    showPhone: false,
    allowMessages: true,
    showActivity: true,
    showFollowersList: true,
    showFollowingList: true,
    showLikesList: true
  };

  // Estados de configuración
  const [privacySettings, setPrivacySettings] = useState(DEFAULT_PRIVACY_SETTINGS);

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    commentNotifications: true,
    likeNotifications: true,
    followNotifications: true,
    mentionNotifications: true,
    newsletterEmails: false
  });

  const [preferences, setPreferences] = useState({
    theme: 'system', // light, dark, system
    language: 'es',
    autoplayVideos: true,
    showNSFW: false
  });

  // Cargar configuraciones al montar el componente
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await usersAPI.getSettings();
      if (response.data) {
        setPrivacySettings({
          ...DEFAULT_PRIVACY_SETTINGS,
          ...(response.data.privacy || {})
        });
        setNotificationSettings(response.data.notifications || notificationSettings);
        setPreferences(response.data.preferences || preferences);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Si falla, usar configuraciones por defecto
    }
  };

  const saveSettings = async (type, settings) => {
    try {
      await usersAPI.updateSettings({
        type,
        settings
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar configuración');
    }
  };

  // Verificar si el usuario tiene un rol especial (staff/admin)
  const isStaffOrAdmin = user && (
    ['superadmin', 'ceo', 'coo', 'head_of_content', 'community_lead', 'moderator'].includes(user.hierarchical_role)
  );

  const handleDeleteAccount = async () => {
    if (confirmText !== 'ELIMINAR') {
      toast.error('Por favor escribe "ELIMINAR" para confirmar');
      return;
    }

    try {
      setLoading(true);
      await authAPI.deleteAccount();
      logout();
      navigate('/');
      toast.success('Tu cuenta ha sido eliminada permanentemente');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Error al eliminar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendAccount = async () => {
    if (confirmText !== 'SUSPENDER') {
      toast.error('Por favor escribe "SUSPENDER" para confirmar');
      return;
    }

    try {
      setLoading(true);
      await authAPI.suspendAccount();
      logout();
      navigate('/');
      toast.success('Tu cuenta ha sido suspendida temporalmente');
    } catch (error) {
      console.error('Error suspending account:', error);
      toast.error('Error al suspender la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyToggle = (setting) => {
    const newSettings = {
      ...privacySettings,
      [setting]: !privacySettings[setting]
    };
    setPrivacySettings(newSettings);
    saveSettings('privacy', newSettings);
    toast.success('Configuración de privacidad actualizada');
  };

  const handleNotificationToggle = (setting) => {
    const newSettings = {
      ...notificationSettings,
      [setting]: !notificationSettings[setting]
    };
    setNotificationSettings(newSettings);
    saveSettings('notifications', newSettings);
    toast.success('Configuración de notificaciones actualizada');
  };

  const handlePreferenceChange = (setting, value) => {
    const newPreferences = {
      ...preferences,
      [setting]: value
    };
    setPreferences(newPreferences);
    saveSettings('preferences', newPreferences);
    toast.success('Preferencia actualizada');
    
    // Aplicar tema inmediatamente
    if (setting === 'theme') {
      applyTheme(value);
    }
  };

  const applyTheme = (theme) => {
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else if (theme === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    } else {
      // Sistema - detectar preferencia del navegador
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.exportData();
      
      // Crear un enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `graphyra-data-${user.username}-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Tus datos han sido descargados');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Error al exportar datos');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'privacy', name: 'Privacidad', icon: <FiEye /> },
    { id: 'notifications', name: 'Notificaciones', icon: <FiBell /> },
    { id: 'security', name: 'Seguridad', icon: <FiShield /> },
    { id: 'preferences', name: 'Preferencias', icon: <FiGlobe /> },
    { id: 'account', name: 'Cuenta', icon: <FiLock /> }
  ];

  if (isStaffOrAdmin && activeTab === 'account') {
    return (
      <div className="account-settings-container">
        <div className="settings-header">
          <h1>Configuración</h1>
          <p>Administra tu cuenta y preferencias</p>
        </div>

        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.name}</span>
            </button>
          ))}
        </div>

        <div className="staff-restriction-message">
          <FiAlertTriangle size={48} />
          <h2>Acceso Restringido</h2>
          <p>
            Las cuentas con roles administrativos no pueden ser eliminadas o suspendidas.
            <br />
            Contacta con el superadministrador si necesitas realizar cambios en tu cuenta.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-settings-container">
      <div className="settings-header">
        <h1>Configuración</h1>
        <p>Administra tu cuenta y preferencias</p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Tab: Privacidad */}
      {activeTab === 'privacy' && (
        <div className="settings-content">
          <section className="settings-section">
            <h2>Configuración de Privacidad</h2>
            <p className="section-description">
              Controla quién puede ver tu información y cómo interactuar contigo
            </p>

            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Perfil Público</h3>
                    <p>Permite que otros usuarios vean tu perfil</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.profileVisible}
                    onChange={() => handlePrivacyToggle('profileVisible')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Mostrar Correo Electrónico</h3>
                    <p>Visible en tu perfil público</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.showEmail}
                    onChange={() => handlePrivacyToggle('showEmail')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Mostrar Teléfono</h3>
                    <p>Visible en tu perfil público</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.showPhone}
                    onChange={() => handlePrivacyToggle('showPhone')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Permitir Mensajes</h3>
                    <p>Otros usuarios pueden enviarte mensajes directos</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.allowMessages}
                    onChange={() => handlePrivacyToggle('allowMessages')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Actividad Pública</h3>
                    <p>Mostrar tu actividad reciente (comentarios, likes)</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.showActivity}
                    onChange={() => handlePrivacyToggle('showActivity')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Lista de Seguidores</h3>
                    <p>Permitir ver quién te sigue</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.showFollowersList}
                    onChange={() => handlePrivacyToggle('showFollowersList')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Lista de Siguiendo</h3>
                    <p>Permitir ver a quién sigues</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.showFollowingList}
                    onChange={() => handlePrivacyToggle('showFollowingList')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Lista de Me gusta</h3>
                    <p>Permitir ver las publicaciones que te gustaron</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={privacySettings.showLikesList}
                    onChange={() => handlePrivacyToggle('showLikesList')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Tab: Notificaciones */}
      {activeTab === 'notifications' && (
        <div className="settings-content">
          <section className="settings-section">
            <h2>Configuración de Notificaciones</h2>
            <p className="section-description">
              Gestiona cómo y cuándo quieres recibir notificaciones
            </p>

            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Notificaciones por Email</h3>
                    <p>Recibe notificaciones importantes por correo</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.emailNotifications}
                    onChange={() => handleNotificationToggle('emailNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Notificaciones Push</h3>
                    <p>Recibe notificaciones en tiempo real</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.pushNotifications}
                    onChange={() => handleNotificationToggle('pushNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Comentarios</h3>
                    <p>Cuando alguien comenta tu contenido</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.commentNotifications}
                    onChange={() => handleNotificationToggle('commentNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Reacciones</h3>
                    <p>Cuando alguien reacciona a tu contenido</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.likeNotifications}
                    onChange={() => handleNotificationToggle('likeNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Nuevos Seguidores</h3>
                    <p>Cuando alguien te sigue</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.followNotifications}
                    onChange={() => handleNotificationToggle('followNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Menciones</h3>
                    <p>Cuando alguien te menciona</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.mentionNotifications}
                    onChange={() => handleNotificationToggle('mentionNotifications')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiBell size={20} />
                  <div>
                    <h3>Boletín Informativo</h3>
                    <p>Recibe noticias y actualizaciones de Graphyra</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationSettings.newsletterEmails}
                    onChange={() => handleNotificationToggle('newsletterEmails')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Tab: Seguridad */}
      {activeTab === 'security' && (
        <div className="settings-content">
          <section className="settings-section">
            <h2>Seguridad de la Cuenta</h2>
            <p className="section-description">
              Protege tu cuenta con opciones de seguridad avanzadas
            </p>

            <div className="security-options">
              <div className="security-card">
                <FiLock size={28} />
                <h3>Cambiar Contraseña</h3>
                <p>Actualiza tu contraseña regularmente</p>
                <button 
                  className="btn-primary-outline"
                  onClick={() => navigate('/settings/profile')}
                >
                  Ir a Editar Perfil
                </button>
              </div>

              <div className="security-card">
                <FiShield size={28} />
                <h3>Verificación en Dos Pasos</h3>
                <p>Próximamente disponible</p>
                <button className="btn-primary-outline" disabled>
                  Activar (Próximamente)
                </button>
              </div>

              <div className="security-card">
                <FiDownload size={28} />
                <h3>Exportar Datos</h3>
                <p>Descarga una copia de tus datos</p>
                <button 
                  className="btn-primary-outline"
                  onClick={handleExportData}
                >
                  Descargar Datos
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Tab: Preferencias */}
      {activeTab === 'preferences' && (
        <div className="settings-content">
          <section className="settings-section">
            <h2>Preferencias de la Aplicación</h2>
            <p className="section-description">
              Personaliza tu experiencia en Graphyra
            </p>

            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <FiMoon size={20} />
                  <div>
                    <h3>Tema de Apariencia</h3>
                    <p>Elige cómo quieres ver Graphyra</p>
                  </div>
                </div>
                <select
                  className="setting-select"
                  value={preferences.theme}
                  onChange={(e) => handlePreferenceChange('theme', e.target.value)}
                >
                  <option value="light">Claro</option>
                  <option value="dark">Oscuro</option>
                  <option value="system">Automático</option>
                </select>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiGlobe size={20} />
                  <div>
                    <h3>Idioma</h3>
                    <p>Selecciona tu idioma preferido</p>
                  </div>
                </div>
                <select
                  className="setting-select"
                  value={preferences.language}
                  onChange={(e) => handlePreferenceChange('language', e.target.value)}
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                </select>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEye size={20} />
                  <div>
                    <h3>Reproducción Automática</h3>
                    <p>Reproducir videos automáticamente</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.autoplayVideos}
                    onChange={() => handlePreferenceChange('autoplayVideos', !preferences.autoplayVideos)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <FiEyeOff size={20} />
                  <div>
                    <h3>Contenido Sensible</h3>
                    <p>Mostrar contenido marcado como sensible</p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.showNSFW}
                    onChange={() => handlePreferenceChange('showNSFW', !preferences.showNSFW)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Tab: Cuenta (Zona de Peligro) */}
      {activeTab === 'account' && (
        <div className="settings-content">
          {/* Suspend Account Section */}
          <section className="settings-section danger-zone">
            <div className="section-header">
              <FiPauseCircle size={24} />
              <div>
                <h2>Suspender Cuenta Temporalmente</h2>
                <p>Desactiva tu cuenta temporalmente. Puedes reactivarla cuando quieras.</p>
              </div>
            </div>

            <div className="section-content">
              <div className="info-box warning-box">
                <h3>¿Qué sucede al suspender tu cuenta?</h3>
                <ul>
                  <li>Tu perfil y publicaciones serán ocultados</li>
                  <li>No podrás iniciar sesión hasta reactivarla</li>
                  <li>Tus datos permanecen guardados de forma segura</li>
                  <li>Puedes reactivar tu cuenta en cualquier momento</li>
                </ul>
              </div>

              <button 
                onClick={() => setShowSuspendModal(true)} 
                className="btn-danger-outline"
              >
                <FiPauseCircle size={18} />
                Suspender Mi Cuenta
              </button>
            </div>
          </section>

          {/* Delete Account Section */}
          <section className="settings-section danger-zone">
            <div className="section-header">
              <FiTrash2 size={24} />
              <div>
                <h2>Eliminar Cuenta Permanentemente</h2>
                <p>Esta acción no se puede deshacer. Procede con precaución.</p>
              </div>
            </div>

            <div className="section-content">
              <div className="info-box danger-box">
                <FiAlertTriangle size={24} />
                <h3>⚠️ Advertencia: Esta acción es irreversible</h3>
                <ul>
                  <li><strong>Todos tus datos serán eliminados permanentemente</strong></li>
                  <li>Tus publicaciones y comentarios serán eliminados</li>
                  <li>No podrás recuperar tu cuenta ni tus datos</li>
                  <li>Tu nombre de usuario quedará disponible para otros</li>
                </ul>
              </div>

              <button 
                onClick={() => setShowDeleteModal(true)} 
                className="btn-danger"
              >
                <FiTrash2 size={18} />
                Eliminar Mi Cuenta
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Modales existentes */}
      {/* Suspend Account Modal */}
      {showSuspendModal && (
        <div className="modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <FiPauseCircle size={32} />
              <h2>Suspender Cuenta</h2>
            </div>
            
            <div className="modal-body">
              <p>
                Tu cuenta será desactivada temporalmente. Puedes reactivarla en cualquier momento
                iniciando sesión nuevamente.
              </p>

              <div className="confirmation-input">
                <label>Escribe <strong>SUSPENDER</strong> para confirmar:</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="SUSPENDER"
                  autoFocus
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                onClick={() => {
                  setShowSuspendModal(false);
                  setConfirmText('');
                }} 
                className="btn-cancel"
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                onClick={handleSuspendAccount} 
                className="btn-danger"
                disabled={loading || confirmText !== 'SUSPENDER'}
              >
                {loading ? 'Suspendiendo...' : 'Suspender Cuenta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header danger-header">
              <FiTrash2 size={32} />
              <h2>Eliminar Cuenta Permanentemente</h2>
            </div>
            
            <div className="modal-body">
              <div className="warning-banner">
                <FiAlertTriangle size={24} />
                <strong>¡Atención! Esta acción es irreversible</strong>
              </div>

              <p>
                Toda tu información será eliminada permanentemente y no podrás recuperar tu cuenta.
              </p>

              <div className="confirmation-input">
                <label>Escribe <strong>ELIMINAR</strong> para confirmar:</label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="ELIMINAR"
                  autoFocus
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmText('');
                }} 
                className="btn-cancel"
                disabled={loading}
              >
                Cancelar
              </button>
              <button 
                onClick={handleDeleteAccount} 
                className="btn-danger"
                disabled={loading || confirmText !== 'ELIMINAR'}
              >
                {loading ? 'Eliminando...' : 'Eliminar Permanentemente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
